import RBAC                                                                                         from '@pixul/hestia-rbac';
import { useUserStore }                                                                             from '@/store/user';
import { PROFORMA_STATUS_BILLED }                                                                   from '@/composables/proforma.js';
import { FOLDER_STATUS_ADV_SIGNED, FOLDER_STATUS_CALCULATING_COMMISSIONS, FOLDER_STATUS_COMPLETED } from '@/composables/folder.js';
import { THIRD_PARTY_INVOICE_STATUS_CANCELLED, THIRD_PARTY_INVOICE_STATUS_TODO }                    from '@/composables/thirdPartyInvoice.js';
import { AGENT_VALIDATION_STATUS_VERIFIED }                                                         from '@/composables/agent.js';

/**
 * @description Functions to calculate dynamic permission for Element
 * @typedef permissionElement
 * @private
 * @static
 */
export const dynamicPermissions = {
  /**
   * @private
   * @param {Object} dossier
   * @return {string[]}
   */
  showPartnerInfos(dossier) {

    if ([FOLDER_STATUS_ADV_SIGNED, FOLDER_STATUS_CALCULATING_COMMISSIONS, FOLDER_STATUS_COMPLETED].includes(dossier.status.key) ||
      dossier.validationByHeadOffice?.key === 'validated') {
      return [];
    }

    return [RBAC.ROLE_SST_MEMBER, RBAC.ROLE_SALE_MEMBER, RBAC.ROLE_VEFA_MEMBER, RBAC.ROLE_CONTRACT, RBAC.ROLE_TRACFIN_REFERENT, RBAC.ROLE_ACCOUNTING, RBAC.ROLE_PROFORMA];
  },

  /**
   * @private
   * @param {Object} dossier
   * @return {string[]}
   */
  uploadRib(dossier) {

    const store = useUserStore();

    const roles = [RBAC.ROLE_ACCOUNTING];

    if (dossier?.participantsIds?.includes(store.user?.id)) {
      return [];
    }

    return roles;
  },

  /**
   * @private
   * @param {Object} dossier
   * @return {string[]}
   */
  uploadDiagnosticReimbursmentInvoice(dossier) {

    const store = useUserStore();

    const roles = [RBAC.ROLE_ACCOUNTING];

    if (dossier?.participantsIds?.includes(store.user?.id)) {
      return [];
    }

    return roles;
  },
  /**
   * @private
   * @param {Object} dossier
   * @param {Object} task
   * @return {string[]}
   */
  showTask(dossier, task) {

    const store = useUserStore();

    const roles = [RBAC.ROLE_CONTRACT, RBAC.ROLE_TRACFIN_REFERENT, RBAC.ROLE_ACCOUNTING, RBAC.ROLE_PROFORMA];

    if (dossier.vefa) {
      roles.push(RBAC.ROLE_VEFA_MEMBER);
    }

    if (task?.assigned?.key === store.user?.id) {
      return [];
    }

    return roles;
  },
  /**
   * @private
   * @param {Object} dossier
   * @return {string[]}
   */
  createTask(dossier) {

    const roles = [RBAC.ROLE_CONTRACT, RBAC.ROLE_TRACFIN_REFERENT, RBAC.ROLE_ACCOUNTING, RBAC.ROLE_PROFORMA];

    if (dossier.vefa) {
      roles.push(RBAC.ROLE_VEFA_MEMBER);
    }

    return roles;
  },
  /**
   * @private
   * @param {Object} dossier
   * @param {Object} task
   * @return {string[]}
   */
  updateTaskInfos(dossier, task) {

    const store = useUserStore();

    const roles = [RBAC.ROLE_CONTRACT, RBAC.ROLE_TRACFIN_REFERENT, RBAC.ROLE_ACCOUNTING, RBAC.ROLE_PROFORMA];

    if (store?.user?.roles?.includes(RBAC.ROLE_SALE_MEMBER) || store?.overrideRoles?.value?.includes(RBAC.ROLE_SALE_MEMBER) || store?.user?.roles?.includes(RBAC.ROLE_SST_MEMBER) || store?.overrideRoles?.value?.includes(RBAC.ROLE_SST_MEMBER)) {
      return [];
    }

    if (dossier?.vefa) {
      roles.push(RBAC.ROLE_VEFA_MEMBER);
    }

    if (task?.assigned?.key === store.user?.id) {
      return [];
    }

    return roles;
  },
  /**
   * @private
   * @param {Object} dossier
   * @return {string[]}
   */
  updateDocuments(dossier) {

    const roles = [RBAC.ROLE_CONTRACT, RBAC.ROLE_TRACFIN_REFERENT];

    if (dossier.vefa) {
      roles.push(RBAC.ROLE_VEFA_MEMBER);
    }

    return roles;
  },
  /**
   * @private
   * @param {Object} dossier
   * @return {string[]}
   */
  sendUploadLink(dossier) {

    const roles = [RBAC.ROLE_CONTRACT];

    if (dossier.vefa) {
      roles.push(RBAC.ROLE_VEFA_MEMBER);
    }

    return roles;
  },

  /**
   * @private
   * @param {Object} dossier
   * @return {string[]}
   */
  sendToNotary(dossier) {

    const roles = [RBAC.ROLE_CONTRACT];

    if (dossier.vefa) {
      roles.push(RBAC.ROLE_VEFA_MEMBER);
    }

    return roles;
  },

  /**
   * @private
   * @param {Object} dossier
   * @return {string[]}
   */
  showCommissionsGeneralParameters(dossier) {

    const roles = [RBAC.ROLE_SST_MEMBER, RBAC.ROLE_SALE_MEMBER, RBAC.ROLE_ACCOUNTING, RBAC.ROLE_PROFORMA];

    if ([FOLDER_STATUS_ADV_SIGNED, FOLDER_STATUS_CALCULATING_COMMISSIONS, FOLDER_STATUS_COMPLETED].includes(dossier.status?.key) && dossier.validationByHeadOffice?.key === 'validated') {

      return [];
    }

    if (dossier.vefa) {
      roles.push(RBAC.ROLE_VEFA_MEMBER);
    }

    return roles;
  },
  /**
   * @private
   * @param {Object} dossier
   * @return {string[]}
   */
  showActorsParameters(dossier) {

    const roles = [RBAC.ROLE_SALE_MEMBER, RBAC.ROLE_CONTRACT, RBAC.ROLE_ACCOUNTING, RBAC.ROLE_PROFORMA];

    if (dossier.validationByHeadOffice.key === 'validated') {
      return [];
    }

    return roles;
  },

  /**
   * @private
   * @param {Object} commission
   * @return {string[]}
   */
  showCommissionBreakdown(commission) {

    const store = useUserStore();

    const roles = [RBAC.ROLE_SALE_MEMBER, RBAC.ROLE_ACCOUNTING, RBAC.ROLE_PROFORMA];

    if (commission.owner === store.user?.id || commission?.agent?.key === store.user?.id) {
      return [];
    }

    if (commission.proforma?.recipientType === 'ada_partner') {

      roles.push(RBAC.ROLE_APPORTEUR);
    }

    return roles;
  },

  changeCommissionProformaStatus(proforma) {

    const roles = [];

    if (proforma?.status !== PROFORMA_STATUS_BILLED) {

      roles.push(RBAC.ROLE_PROFORMA);
    }

    return roles.length > 0 ? roles : null;
  },

  uploadInvoice(commission) {

    const store = useUserStore();

    const roles = [RBAC.ROLE_PROFORMA, RBAC.ROLE_ACCOUNTING];

    if (commission.owner === store.user?.id) {
      return [];
    }

    if (commission.proforma?.recipientType === 'ada_partner') {

      roles.push(RBAC.ROLE_APPORTEUR);
    }

    return roles;
  },

  changeCommissionAgentValidation(actor) {

    const store = useUserStore();

    if (actor.userId === store.user?.id && actor.agentValidation !== AGENT_VALIDATION_STATUS_VERIFIED) {
      return [];
    }

    return [RBAC.ROLE_PROFORMA];
  },

  thirdPartyInvoiceIfParticipantOrVefa(invoice, dossier) {

    const store = useUserStore();

    const roles = [RBAC.ROLE_CONTRACT, RBAC.ROLE_ACCOUNTING, RBAC.ROLE_PROFORMA];

    if (dossier?.participantsIds?.includes(store.user?.id) &&
      invoice.status.key !== THIRD_PARTY_INVOICE_STATUS_TODO &&
      invoice.status.key !== THIRD_PARTY_INVOICE_STATUS_CANCELLED) {
      return [];
    }

    if (invoice.agent?.key === store.user?.id) {
      return [];
    }

    if (dossier?.vefa) {
      roles.push(RBAC.ROLE_VEFA_MEMBER);
    }

    return roles.length > 0 ? roles : null;
  },

  thirdPartyInvoiceIfParticipant(invoice) {

    const store = useUserStore();

    const roles = [RBAC.ROLE_ACCOUNTING, RBAC.ROLE_PROFORMA];

    if (invoice.agent?.key === store.user?.id) {
      return [];
    }

    return roles;
  },

  updateFees(dossier) {

    const roles = [RBAC.ROLE_CONTRACT, RBAC.ROLE_PROFORMA];

    if (dossier?.compromiseSigned || dossier?.validationByHeadOffice?.key === 'validated') {
      return null;
    }

    return roles;
  }

};
